import {concat, isUndefined, uniq} from 'lodash';
import React, {Component} from 'react';
import {flushSync} from 'react-dom';
import {connect} from 'react-redux';
import {Diff} from 'utility-types';

import {isElectron} from '../../../../libs/shared/core/src/helpers/environment/platformHelpers';
import {CustomError} from '../../../../libs/shared/core/src/helpers/types/customErrors';
import {emptyCleanHtml} from '../../../../libs/shared/core/src/helpers/typescript/cleanHtml';
import {CancelToken} from '../../../../libs/shared/core/src/helpers/utilities/asyncCancelHelpers';
import {
  AttachmentModel,
  UploadedAttachmentModel,
} from '../../../../libs/shared/core/src/models/api/attachmentModel';
import {ChannelModel} from '../../../../libs/shared/core/src/models/api/channelModel';
import {CompanyModel} from '../../../../libs/shared/core/src/models/api/company/companyModel';
import {
  MessageErrorModel,
  MessageErrorsEnum,
  MessageErrorSourcesEnum,
} from '../../../../libs/shared/core/src/models/api/messageErrorModel';
import {
  DraftMessageModel,
  SendActionsEnum,
  SendMessageOptionsModel,
} from '../../../../libs/shared/core/src/models/api/messageModel';
import {
  globalNamespace,
  isTeammateNamespace,
  isTeamNamespace,
} from '../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {composerLocation} from '../../../../libs/shared/core/src/models/tracking/contexts/composerTracking';
import {reportToBugsnag} from '../../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {fetchApiAsync} from '../../../../libs/shared/core/src/services/http/http';
import {wrapInDataEventContext} from '../../../../libs/shared/core/src/services/tracking/dataEventTrackingProvider';
import {
  InterfaceEventsEnum,
  NamespaceTypesEnum,
  ReplyLocationsEnum,
  trackInterfaceEvent,
} from '../../../../libs/shared/core/src/services/tracking/tracking';
import {
  getHasNewSynchronousP3,
  getHasTwitterOauth2,
} from '../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {wrapActionCreator} from '../../../../libs/shared/core/src/store/helpers/reduxHelpers';
import {
  CellsContext,
  makeDefaultCellsContext,
} from '../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {
  ComposerPanelId,
  PanelTypesEnum,
  RViewerPanelId,
  ViewerPanelId,
} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {
  AppParametricSelector,
  createSelector,
  makeMapStateToProps,
  wrapInShallowSelector,
} from '../../../../srcCommon/helpers/reselectHelpers';
import {delayAsync} from '../../../helpers/asyncHelpers';
import {
  ChannelErrorsEnum,
  isReplyAllowedWithoutInboxAccess,
  isTeamChannel,
} from '../../../helpers/models/channelHelpers';
import {findConversationsCanceledReminder} from '../../../helpers/models/conversationHelpers';
import {
  buildMessagePatcherFactory,
  MessagePatch,
  MessagePatcherFactory,
} from '../../../helpers/models/messagePatchHelpers';
import {
  AddRecipients,
  computeNewChannelRecipients,
  RemoveRecipients,
  ReplyTypesEnum,
  VisualRecipientMap,
} from '../../../helpers/models/recipientHelpers';
import {buildTagSearchUrl} from '../../../helpers/models/tagHelpers';
import {ConversationModel} from '../../../models/api/conversationModel';
import {LocalPreferencesFlagsEnum} from '../../../models/api/localPreferencesModel';
import {DefaultSendActionsEnum, StartGuideTaskTypesEnum} from '../../../models/api/preferencesModel';
import {PermissionsEnum} from '../../../models/api/roleModel';
import {ApiSearchTags, SearchTagModel} from '../../../models/api/searchTagModel';
import {TagBehaviorsEnum, TagModel} from '../../../models/api/tagModel';
import {deleteMessageTask} from '../../../services/tasks/deleteMessageTask';
import {reloadMessageTask} from '../../../services/tasks/reloadMessageTask';
import {ShareMessageOptions, shareMessageTask} from '../../../services/tasks/shareMessageTask';
import {AppState} from '../../../store/appState';
import {Dispatch} from '../../../store/appStore';
import {getHasPermission} from '../../../store/data/access/accessSelectors';
import {makeGetFilteredEntryPoints} from '../../../store/data/applications/applicationsSelectors';
import {getUserParticipatingChannels} from '../../../store/data/channels/channelsSelectors';
import {getCompany, getHasTrialExpired} from '../../../store/data/company/companySelectors';
import {makeGetVisualRecipientMap} from '../../../store/data/contacts/recipientsSelectors';
import {getConversations} from '../../../store/data/dataSelectors';
import {toggleFlagAction} from '../../../store/data/localPreferences/localPreferencesActions';
import {getFlags} from '../../../store/data/localPreferences/localPreferencesSelectors';
import {
  ExistingDraftStrategiesEnum,
  replyToMessageAction,
} from '../../../store/data/messages/draftMessagesActions';
import {
  makeGetAuthor,
  makeGetDraftChannel,
  makeGetIsMessageVisuallyShared,
  makeGetVariableInterpolator,
} from '../../../store/data/messages/messagesSelectors';
import {saveMessageAsyncAction, SaveMessageOptions} from '../../../store/data/messages/saveMessageActions';
import {sendMessageAsyncAction, SendMessageOptions} from '../../../store/data/messages/sendMessageActions';
import {
  updateShouldSendOnEnterForChatAction,
  updateShouldShowNewTagInCellsAction,
  updateStartGuideTasksAction,
} from '../../../store/data/preferences/preferencesActions';
import {
  getDefaultSendAction,
  getPasteAsText,
  getShouldSendOnEnterForChat,
  getShouldWarnManyRecipients,
  getShouldWarnMissingAttachments,
} from '../../../store/data/preferences/preferencesSelectors';
import {createTagAction} from '../../../store/data/tags/saveTagActions';
import {getUserAndDelegatedInboxTagIds} from '../../../store/data/tags/tagsSelectors';
import {getIsUserVisitor, getUser, getUserNamespace} from '../../../store/data/teammates/teammatesSelectors';
import {
  getHasUserTeamTrialExpired,
  getTeamsByNamespace,
  getUserTeams,
} from '../../../store/data/teams/teamsSelectors';
import {showWorkspaceTagAsyncAction} from '../../../store/data/workspace/workspaceActions';
import {popoutComposerAction} from '../../../store/panels/composer/composerPanelActions';
import {
  getPanel,
  makeGetIsComposerPanelPopout,
  makeGetIsComposerPanelPopup,
  makeGetIsStandalonePanel,
} from '../../../store/panels/panelsSelectors';
import {viewerConversationCloseRequestedAction} from '../../../store/panels/viewer/viewerPanelActions';
import {popoutConversationTransitionViewerAction} from '../../../store/panels/viewer/viewerPanelConversationsActions';
import {computeTimelineVariation} from '../../../store/timelineVariations/timelineVariationSelectors';
import {AttachmentsTransferProps, AttachmentsUploader} from '../../common/attachments/attachmentsUploader';
import {TagTemplate} from '../../common/tags/helpers';
import {ChannelSaver, SaveChannelOptions} from './channelPicker/pickChannelDropdownContainer';
import {Composer, ComposerProps} from './composer';
import {ShareMessage} from './share/shareButtonCoordinator';
import {SendMessage} from './toolbar/buttons/sendToolbarButton';
import {DraftSaveStatusesEnum} from './toolbar/draftSaveIndicator';

/*
 * Props.
 */

interface ComposerContainerProps {
  panelId: ComposerPanelId | ViewerPanelId;
  message: DraftMessageModel;
  cellsContext?: CellsContext;
  conversation: ConversationModel | null;
  shouldDynamicallyGrowComposer?: boolean;
  /** Whether to show the popout button in the composer header. */
  canPopout: boolean;
}

interface ComposerVersionedProps {
  saveMessageAsync: (patch: MessagePatch) => Promise<void>;
  sendMessage: (arg: Partial<SendMessageOptionsModel> | undefined) => void;
  saveSubject: (arg: string) => void;
  saveChannel: (options: SaveChannelOptions) => void;
  shareMessage: () => void;
  doneWithMessage: () => void;
  addRecipients: AddRecipients;
  removeRecipients: RemoveRecipients;
  addAttachment: (model: UploadedAttachmentModel) => void;
  onRemoveAttachmentClick: (arg: string) => void;
  reloadMessage: () => void;
  createTag: (template: TagTemplate) => Promise<void>;
  tagMessage: (model: SearchTagModel) => void;
  untagMessage: (model: TagModel) => void;
  selectedTagIds: ReadonlyArray<number>;
  getVersion: () => string | undefined;
  setVersion: (version?: string) => void;
  draftSaveStatus: DraftSaveStatusesEnum;
  isDraftSaving: boolean;
}

type ComposerContainerComponentBaseProps = Diff<
  ComposerProps,
  ComposerVersionedProps & AttachmentsTransferProps
>;
type MessageSaver = (options: SaveMessageOptions) => Promise<DraftMessageModel | undefined>;
type MessageSender = (sendOptions: Partial<SendMessageOptionsModel>, options: SendMessageOptions) => void;
type MessageSharer = (options: ShareMessageOptions) => void;
type MessageLoader = () => Promise<DraftMessageModel | undefined>;
type TagSaver = (template: TagTemplate) => Promise<TagModel | undefined>;

interface ComposerContainerComponentProps extends ComposerContainerComponentBaseProps {
  company: CompanyModel;
  recipients: VisualRecipientMap;
  attachments: ReadonlyArray<AttachmentModel>;
  channel: ChannelModel;
  saveMessage: MessageSaver;
  sendMessage: MessageSender;
  reloadMessage: MessageLoader;
  shareMessage: MessageSharer;
  saveTag: TagSaver;
}

class SaveTimeoutError extends CustomError {
  constructor(messageUid: string) {
    super(`Timeout while saving message draft ${messageUid}`);
  }
}

/*
 * Constants.
 */

/** Interval during which we allow CANT_SAVE errors before showing a banner. */
const saveErrorGracePeriod = 72 * 1000; // 72s. (30s HTTP + 4s Offline + 2s Buffer) * 2 in-flight requests.

/*
 * Component.
 */

interface ComposerContainerComponentState {
  loadKey: number;
  timeOfLastSave?: number;
  activeSaveCount: number;
  saveError?: MessageErrorModel;
  selectedTagIds: ReadonlyArray<number>;
  isFormattingToolbarOpen: boolean;
}

class ComposerContainerComponent extends Component<
  ComposerContainerComponentProps,
  ComposerContainerComponentState
> {
  constructor(props: ComposerContainerComponentProps) {
    super(props);
    this.state = {
      loadKey: 0,
      activeSaveCount: 0,
      saveError: props.message.error,
      selectedTagIds: [],
      isFormattingToolbarOpen: Boolean(props.isFormattingToolbarOpen),
    };
    this.patcherFactory = buildMessagePatcherFactory();
  }

  // Track the current version to detect edit conflicts.
  private version?: string;
  private patcherFactory: MessagePatcherFactory;
  private isMounted: boolean = false;

  /*
   * Event handlers.
   */

  private readonly updateActiveSaveCount = (saveCountUpdater: (previousSaveCount: number) => number) => {
    flushSync(() => {
      // Since the message saving state is fragmented between React state (here) and Redux state (via the draft message),
      // we need to make sure that we update this state syncronously so that it happens before the Redux state
      // is updated.
      this.setState(({activeSaveCount}) => ({activeSaveCount: saveCountUpdater(activeSaveCount)}));
    });
  };

  private readonly saveMessageAsync = async (messagePatch: MessagePatch) => {
    this.updateActiveSaveCount((previousSaveCount) => previousSaveCount + 1);
    try {
      const timeOfThisSave = Date.now();

      // Pass the last known version: this will fail if the draft was edited independently.
      const newDraftPromise = this.props.saveMessage({
        patchMessage: this.patcherFactory(messagePatch),
        newRecipients: messagePatch.newRecipients || messagePatch.addedRecipients,
        getVersion: this.getVersion,
        setVersion: this.setVersion,
      });

      const timeoutErrorPromise = delayAsync(saveErrorGracePeriod).then(() => {
        throw new SaveTimeoutError(this.props.message.uid);
      });

      // Show an error if the save takes longer than the grace period, but still allow the save to finish.
      Promise.race([newDraftPromise, timeoutErrorPromise]).catch((error) => {
        // Report this error only if it happens before the save promise resolves.
        if (error instanceof SaveTimeoutError) {
          reportToBugsnag(error);
        }

        if (!this.isMounted) {
          return;
        }

        this.setState({
          saveError: {
            source: MessageErrorSourcesEnum.CLIENT,
            type: MessageErrorsEnum.CANT_SAVE,
          },
        });
      });

      this.setState(({timeOfLastSave}) => {
        // If this is our first save, start our grace period timer now.
        if (isUndefined(timeOfLastSave)) {
          return {timeOfLastSave: timeOfThisSave};
        }

        return null;
      });

      const newDraft = await newDraftPromise;
      if (!newDraft || !this.isMounted) {
        return;
      }
      this.setState(({timeOfLastSave}) => {
        // This may have happened after a reload, in which case we don't care.
        if (isUndefined(timeOfLastSave)) {
          return null;
        }

        // Record the successful save, and clear the error state.
        const {error} = newDraft;
        if (!error) {
          return {timeOfLastSave: timeOfThisSave, saveError: undefined};
        }

        // Compare to the current wall time, because super long response times count towards our grace period.
        const timeSinceLastSave = Date.now() - timeOfLastSave;

        // Show CANT_SAVE errors after a grace period.
        if (error.type !== MessageErrorsEnum.CANT_SAVE || timeSinceLastSave > saveErrorGracePeriod) {
          return {saveError: error};
        }

        return null;
      });
    } catch (error) {
      reportToBugsnag(error);

      if (!this.isMounted) {
        return;
      }

      this.setState({
        saveError: {
          source: MessageErrorSourcesEnum.CLIENT,
          type: MessageErrorsEnum.CANT_SAVE,
        },
      });
    } finally {
      if (this.isMounted) {
        this.updateActiveSaveCount((previousSaveCount) => previousSaveCount - 1);
      }
    }
  };

  private readonly reloadMessageAsync = async () => {
    this.setState({saveError: undefined});

    // TODO: Add support for cancellation tokens on reloadMessage.
    // TODO: Suffix all async method names with "Async".
    const newDraft = await this.props.reloadMessage();
    if (!newDraft || !this.isMounted) {
      return;
    }

    // Incrementing loadKey will force the editor to update with the latest message.
    this.version = newDraft.version;

    // The linter doesn't see our usage inside the callback-style setState above.
    // eslint-disable-next-line react/no-unused-state
    this.setState(({loadKey}) => ({loadKey: loadKey + 1, timeOfLastSave: undefined, saveError: undefined}));
  };

  private readonly getVersion = () => this.version;

  private readonly setVersion = (version?: string) => {
    this.version = version;
  };

  /*
   * Body, subject, signature, quote.
   */

  private readonly setIsFormattingToolbarOpen: (arg: boolean) => void = (isOpen) => {
    this.setState({isFormattingToolbarOpen: isOpen});
    this.props.setIsFormattingToolbarOpen(isOpen);
  };

  /*
   * Channel.
   */

  private readonly saveChannel: ChannelSaver = (options) => {
    const {channel, recipients, attachments} = this.props;
    const {newChannel, shouldResetContent} = options;

    // In case we'll need to reset the message's content, build the new properties.
    const resetMessageContentPatch: Partial<MessagePatch> = {
      newType: newChannel.messageType,
      newHtml: emptyCleanHtml,
      newText: '',
      removedAttachmentCids: attachments.map((a) => a.cid),
      newRecipients: computeNewChannelRecipients(newChannel, channel, {}) || [],
    };

    // Build the changes to apply to the message.
    const messagePatch: Partial<MessagePatch> = {
      newChannelId: newChannel.id,
      newRecipients: computeNewChannelRecipients(newChannel, channel, recipients),
      ...(shouldResetContent && resetMessageContentPatch),
    };

    this.saveMessageAsync(messagePatch);

    const {conversation, message} = this.props;
    trackInterfaceEvent(InterfaceEventsEnum.COMPOSER_CHANNEL_CHANGE, {
      namespace: newChannel.namespace,
      conversation_id: conversation?.id ?? null,
      message_is_reply: Boolean(message.inReplyToId),
      replied_message_id: message.inReplyToId ?? null,
      from_alias_type: trackingNamespaceTypeForChannel(newChannel),
    });
  };

  /*
   * Recipients.
   */

  private readonly addRecipients: AddRecipients = (addedRecipients) => {
    this.saveMessageAsync({addedRecipients});
  };

  private readonly removeRecipients: RemoveRecipients = (removedRecipients) => {
    this.saveMessageAsync({removedRecipients});
  };

  /*
   * Attachments.
   */

  private readonly addAttachment = (attachment: UploadedAttachmentModel) => {
    this.saveMessageAsync({addedAttachments: [attachment]});
  };

  private readonly removeAttachment = (cid: string) => {
    this.saveMessageAsync({removedAttachmentCids: [cid]});
  };

  /*
   * Tags.
   */

  private readonly tagMessage: (model: SearchTagModel) => void = (searchTag) => {
    this.setState(({selectedTagIds}) => ({
      selectedTagIds: [...selectedTagIds, searchTag[0].id],
    }));
  };

  private readonly untagMessage: (model: TagModel) => void = (tag) => {
    this.setState(({selectedTagIds}) => ({
      selectedTagIds: selectedTagIds.filter((id) => id !== tag.id),
    }));
  };

  private readonly createTag: (template: TagTemplate) => Promise<void> = async (tag) => {
    const newTag = await this.props.saveTag(tag);
    if (!newTag || !this.isMounted) {
      return;
    }

    this.tagMessage([newTag]);
  };

  /*
   * Message.
   */

  private readonly sendMessage: SendMessage = (options) => {
    if (this.state.saveError) {
      return;
    }

    this.props.sendMessage(
      {
        ...options,
        tagIds: this.state.selectedTagIds,
      },
      {
        getVersion: this.getVersion,
        setVersion: this.setVersion,
      },
    );
  };

  private readonly shareMessage: ShareMessage = (trackerIds?, commentText?) => {
    this.props.shareMessage({
      trackerIds,
      commentText,
      getVersion: this.getVersion,
      setVersion: this.setVersion,
    });
  };

  private readonly doneWithMessage = () => {
    // Revert the author to the last non-visitor.
    const {lastNonVisitorAuthorId} = this.props.message;
    this.saveMessageAsync({newAuthorId: lastNonVisitorAuthorId});
  };

  /*
   * Lifecycle.
   */

  componentDidMount() {
    this.isMounted = true;

    // Capture version when starting to edit.
    this.version = this.props.message.version;
  }

  componentWillUnmount() {
    this.isMounted = false;
  }

  /*
   * Render.
   */

  render() {
    return wrapInDataEventContext(
      <AttachmentsUploader
        company={this.props.company}
        attachments={this.props.message.attachments}
        emlAttachments={this.props.message.emlAttachments}
        onAttachmentUploaded={this.addAttachment}
      >
        {(attachmentsProps) => (
          <Composer
            {...this.props}
            {...attachmentsProps}
            key={this.state.loadKey}
            saveChannel={this.saveChannel}
            saveMessageAsync={this.saveMessageAsync}
            addRecipients={this.addRecipients}
            removeRecipients={this.removeRecipients}
            addAttachment={this.addAttachment}
            onRemoveAttachmentClick={this.removeAttachment}
            reloadMessage={this.reloadMessageAsync}
            sendMessage={this.sendMessage}
            shareMessage={this.shareMessage}
            doneWithMessage={this.doneWithMessage}
            saveError={this.state.saveError}
            tagMessage={this.tagMessage}
            untagMessage={this.untagMessage}
            selectedTagIds={this.state.selectedTagIds}
            createTag={this.createTag}
            isFormattingToolbarOpen={this.state.isFormattingToolbarOpen}
            setIsFormattingToolbarOpen={this.setIsFormattingToolbarOpen}
            setVersion={this.setVersion}
            draftSaveStatus={this.computeDraftSaveStatus()}
            isDraftSaving={this.state.activeSaveCount > 0}
          />
        )}
      </AttachmentsUploader>,
      'message_composer',
      {location: composerLocation(this.props.isComposerPopout)},
    );
  }

  private computeDraftSaveStatus() {
    if (this.state.saveError || this.props.message.error) {
      return DraftSaveStatusesEnum.ERROR;
    }

    if (this.props.message.isSaving) {
      return DraftSaveStatusesEnum.SAVED_OFFLINE;
    }

    if (!this.props.message.wasSaved) {
      return DraftSaveStatusesEnum.NEVER_SAVED;
    }

    return DraftSaveStatusesEnum.SAVED;
  }
}

function trackingNamespaceTypeForChannel(channel: ChannelModel): NamespaceTypesEnum {
  return isTeamChannel(channel) ? NamespaceTypesEnum.TEAM : NamespaceTypesEnum.PRIVATE;
}

/*
 * Selectors.
 */

function getPanelId(_state: AppState, props: ComposerContainerProps) {
  return props.panelId;
}

function getMessage(_state: AppState, props: ComposerContainerProps) {
  return props.message;
}

function getProvidedCellsContext(_state: AppState, props: ComposerContainerProps) {
  return props.cellsContext;
}

type GetChannel = AppParametricSelector<any, ChannelModel>;
function makeGetCellsContext(getChannel: GetChannel) {
  return wrapInShallowSelector(
    createSelector([getUser, getChannel, getProvidedCellsContext], (user, channel, cellsContext) => {
      // If a cells context was provided, use it.
      if (cellsContext) {
        return cellsContext;
      }

      // Otherwise, build a new one from the channel.
      return makeDefaultCellsContext(user, isTeamChannel(channel));
    }),
  );
}

function makeGetConversation() {
  return createSelector(
    [getMessage, getConversations],
    (message, conversations) => conversations[message.conversationId],
  );
}

function makeGetNamespace(getChannel: GetChannel, getConversation: ReturnType<typeof makeGetConversation>) {
  return createSelector([getChannel, getMessage, getConversation], (channel, message, conversation) => {
    const isReply = Boolean(message.inReplyToId);
    if (conversation && isReply && isTeamNamespace(conversation.namespace)) {
      return conversation.namespace;
    }

    return channel.namespace;
  });
}

function makeGetIsChannelPrivateToUser(getChannel: GetChannel) {
  return createSelector([getChannel, getUser], (channel, user) => channel.namespace === user.namespace);
}

function makeGetHasClosePermission(
  getNamespace: ReturnType<typeof makeGetNamespace>,
  getIsChannelPrivateToUser: ReturnType<typeof makeGetIsChannelPrivateToUser>,
) {
  return createSelector(
    [getHasPermission, getNamespace, getIsChannelPrivateToUser],
    (hasPermission, namespace, isChannelPrivateToUser) =>
      isChannelPrivateToUser || hasPermission(namespace, PermissionsEnum.CONVERSATION_ARCHIVE),
  );
}

function makeGetCanReplyWithoutInboxAccess(
  getConversation: ReturnType<typeof makeGetConversation>,
  getChannel: GetChannel,
) {
  return createSelector([getConversation, getChannel], (conversation, channel) => {
    if (!conversation) {
      return false;
    }

    const isInConversation = conversation.channelIds.includes(channel.id);

    return isInConversation && isReplyAllowedWithoutInboxAccess(channel);
  });
}

function makeGetHasSendPermission(
  getNamespace: ReturnType<typeof makeGetNamespace>,
  getChannel: GetChannel,
  getIsChannelPrivateToUser: ReturnType<typeof makeGetIsChannelPrivateToUser>,
  getCanReplyWithoutInboxAccess: ReturnType<typeof makeGetCanReplyWithoutInboxAccess>,
) {
  return createSelector(
    [
      getHasPermission,
      getNamespace,
      getChannel,
      getUserParticipatingChannels,
      getIsChannelPrivateToUser,
      getCanReplyWithoutInboxAccess,
    ],
    (
      hasPermission,
      namespace,
      channel,
      userParticipatingChannels,
      isChannelPrivateToUser,
      canReplyWithoutInboxAccess,
    ) => {
      const hasConversationMessagePermission = hasPermission(namespace, PermissionsEnum.CONVERSATION_MESSAGE);
      const isChannelParticipant = userParticipatingChannels.includes(channel);
      const isMissingO365SendPermissions =
        channel?.error?.type === ChannelErrorsEnum.MISSING_O365_SEND_PERMISSIONS;

      return (
        (hasConversationMessagePermission || isChannelPrivateToUser) &&
        (isChannelParticipant || canReplyWithoutInboxAccess) &&
        !isMissingO365SendPermissions
      );
    },
  );
}

function makeGetDefaultSendAction(getHasClosePermission: ReturnType<typeof makeGetHasClosePermission>) {
  return createSelector(
    [getHasClosePermission, getDefaultSendAction],
    (hasClosePermission, defaultSendAction) =>
      hasClosePermission ? defaultSendAction : DefaultSendActionsEnum.SEND,
  );
}

// TODO: DRY with makeGetConversationTagNamespaces in addTopicOrTagDropdownContainer.
function makeGetFetchTagsAsync<P>(
  getChannel: AppParametricSelector<P, ChannelModel>,
  getConversation: AppParametricSelector<P, ConversationModel | undefined>,
) {
  const getTagNamespaces = createSelector(
    [getUserNamespace, getUserTeams, getChannel, getConversation],
    (userNamespace, userTeams, channel, conversation) => {
      // If this is the first message in a conversation, the conversation may not exist or may have a global
      // namespace. In that case, use the channel namespace.
      const namespace =
        conversation && conversation.namespace !== 'global' ? conversation.namespace : channel.namespace;

      if (isTeammateNamespace(namespace)) {
        return [globalNamespace, userNamespace, ...userTeams.map((t) => t.namespace)];
      }

      return uniq([globalNamespace, userNamespace, namespace]);
    },
  );

  const getTagInboxIds = createSelector([getChannel, getConversation], (channel, conversation) =>
    conversation && conversation.inboxIds.length > 0 ? conversation.inboxIds : [channel.inboxId],
  );

  return createSelector(
    [getCompany, getHasPermission, getTagNamespaces, getTagInboxIds, getUserAndDelegatedInboxTagIds],
    (company, hasPermission, tagNamespaces, tagInboxIds, userAndDelegatedInboxTagIds) =>
      async function fetchAsync(
        query: string,
        tags: ReadonlyArray<SearchTagModel | null>,
        cancelToken: CancelToken,
      ) {
        const permittedTagNamespaces = tagNamespaces.filter((namespace) =>
          hasPermission(namespace, PermissionsEnum.APPLY_TAG),
        );
        const tagCount = tags.filter((t) => t).length;

        // Build the URL.
        const url = buildTagSearchUrl(company, query, {
          namespaces: permittedTagNamespaces,
          inboxIds: tagInboxIds,
          offset: tagCount,
          excludeTagIds: userAndDelegatedInboxTagIds,
          behaviors: [TagBehaviorsEnum.CAN_APPLY_FROM_LIST],
          source: 'composer-tags',
        });

        // Fetch the new tags.
        const newTags = await fetchApiAsync(ApiSearchTags, url, {cancelToken});

        return {
          data: concat(tags, newTags.tags),
          canRequestMore: newTags.tags.length === newTags.limit,
        };
      },
  );
}

/** Get the value for the Formatting Toolbar local preferences flag. */
const getIsFormattingToolbarOpen = createSelector(
  [getFlags],
  (flags) => flags[LocalPreferencesFlagsEnum.FORMATTING_TOOLBAR],
);

/** Find the canceled reminder for our user in this conversation. */
function makeGetCanceledReminder(
  getConversation: ReturnType<typeof makeGetConversation>,
  getCellsContext: ReturnType<typeof makeGetCellsContext>,
) {
  return createSelector([getConversation, getCellsContext], (conversation, cellsContext) => {
    if (!conversation) {
      return undefined;
    }

    return findConversationsCanceledReminder(cellsContext, [conversation]);
  });
}

function makeGetShouldAttachExternalResources() {
  return createSelector([getPanel], (panel) =>
    Boolean(panel && panel.type === PanelTypesEnum.COMPOSER && panel.shouldAttachExternalResources),
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<ComposerContainerProps>()(() => {
  const getChannel = makeGetDraftChannel(getMessage);
  const getConversation = makeGetConversation();
  const getCellsContext = makeGetCellsContext(getChannel);
  const getNamespace = makeGetNamespace(getChannel, getConversation);
  const getIsChannelPrivateToUser = makeGetIsChannelPrivateToUser(getChannel);
  const getCanReplyWithoutInboxAccess = makeGetCanReplyWithoutInboxAccess(getConversation, getChannel);
  const getHasClosePermission = makeGetHasClosePermission(getNamespace, getIsChannelPrivateToUser);
  const getDefaultPermittedSendAction = makeGetDefaultSendAction(getHasClosePermission);
  const getCanceledReminder = makeGetCanceledReminder(getConversation, getCellsContext);
  const getAuthor = makeGetAuthor(getMessage);

  return {
    company: getCompany,
    user: getUser,
    channel: getChannel,
    namespace: getNamespace,
    defaultSendAction: getDefaultPermittedSendAction,
    hasClosePermission: getHasClosePermission,
    hasSendPermission: makeGetHasSendPermission(
      getNamespace,
      getChannel,
      getIsChannelPrivateToUser,
      getCanReplyWithoutInboxAccess,
    ),
    hasTrialExpired: getHasTrialExpired,
    hasTeamTrialExpired: getHasUserTeamTrialExpired,
    cellsContext: getCellsContext,
    recipients: makeGetVisualRecipientMap(getNamespace, getMessage),
    shouldSendOnEnter: getShouldSendOnEnterForChat,
    isFormattingToolbarOpen: getIsFormattingToolbarOpen,
    isUserVisitor: getIsUserVisitor,
    interpolator: makeGetVariableInterpolator(getMessage),
    shouldPasteAsText: getPasteAsText,
    fetchTagsAsync: makeGetFetchTagsAsync(getChannel, getConversation),
    teams: getTeamsByNamespace,
    userTeams: getUserTeams,
    shouldWarnManyRecipients: getShouldWarnManyRecipients,
    shouldWarnMissingAttachments: getShouldWarnMissingAttachments,
    canceledReminder: getCanceledReminder,
    author: getAuthor,
    composerEntryPoints: makeGetFilteredEntryPoints(getChannel),
    isComposerPopout: makeGetIsComposerPanelPopout(getPanelId),
    isComposerPopup: makeGetIsComposerPanelPopup(getPanelId),
    isComposerStandalone: makeGetIsStandalonePanel(getPanelId),
    isMessageShared: makeGetIsMessageVisuallyShared(getMessage),
    shouldAttachExternalResources: makeGetShouldAttachExternalResources(),
    hasTwitterOauth2: getHasTwitterOauth2,
    hasNewSynchronousP3: getHasNewSynchronousP3,
  };
});

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    saveMessage: wrapActionCreator(dispatch, saveMessageAsyncAction),
    deleteMessageTask: wrapActionCreator(dispatch, deleteMessageTask),
    sendMessageAsync: wrapActionCreator(dispatch, sendMessageAsyncAction),
    viewerConversationCloseRequested: wrapActionCreator(dispatch, viewerConversationCloseRequestedAction),
    reloadMessageTask: wrapActionCreator(dispatch, reloadMessageTask),
    shareMessageTask: wrapActionCreator(dispatch, shareMessageTask),
    toggleFlag: wrapActionCreator(dispatch, toggleFlagAction),
    updateShouldSendOnEnterForChat: wrapActionCreator(dispatch, updateShouldSendOnEnterForChatAction),
    popoutComposer: wrapActionCreator(dispatch, popoutComposerAction),
    createTag: wrapActionCreator(dispatch, createTagAction),
    showWorkspaceTag: showWorkspaceTagAsyncAction,
    updateShouldShowNewTagInCells: wrapActionCreator(dispatch, updateShouldShowNewTagInCellsAction),
    replyToMessage: wrapActionCreator(dispatch, replyToMessageAction),
    updateStartGuideTasks: wrapActionCreator(dispatch, updateStartGuideTasksAction),
    popoutConversationTransitionViewer: wrapActionCreator(dispatch, popoutConversationTransitionViewerAction),
  };
}

export const ComposerContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  (stateProps, dispatchProps, ownProps) => {
    const {panelId, message, conversation, canPopout} = ownProps;
    const {channel, recipients, hasSendPermission, cellsContext, isComposerPopup} = stateProps;
    const {alwaysShowReplyComposer} = channel.behaviors;

    /*
     * Draft modifications.
     */

    const saveMessage = async (options: SaveMessageOptions) => {
      const {savedMessage} = await dispatchProps.saveMessage(message.uid, options);
      return savedMessage;
    };

    const deleteMessage = () => {
      dispatchProps.deleteMessageTask(message.uid);
    };

    const sendMessage = (sendOptions: Partial<SendMessageOptionsModel>, options: SendMessageOptions) => {
      if (!hasSendPermission) {
        throw new Error('Attempted to send without permission');
      }

      dispatchProps.sendMessageAsync(
        message.uid,
        {
          sendAction: SendActionsEnum.SEND,
          cellsContext,
          ...sendOptions,
        },
        options,
      );

      if (
        RViewerPanelId.is(ownProps.panelId) &&
        ownProps.conversation &&
        sendOptions.sendAction === SendActionsEnum.SEND_AND_ARCHIVE
      ) {
        dispatchProps.viewerConversationCloseRequested(ownProps.panelId, ownProps.conversation.id);
      }

      // If user sends an email, complete the send email start guide task.
      dispatchProps.updateStartGuideTasks(StartGuideTaskTypesEnum.SEND_EMAIL);

      if (conversation?.lastMessage && alwaysShowReplyComposer && RViewerPanelId.is(panelId)) {
        dispatchProps.replyToMessage({
          panelId,
          conversationId: conversation.id,
          replyToUid: conversation.lastMessage.uid,
          replyType: ReplyTypesEnum.SINGLE,
          location: ReplyLocationsEnum.AUTO,
          shouldIgnoreShowReplyComposerBehavior: false,
          // TODO: Test whether the automatic reply composer still works.
          existingDraftStrategy: ExistingDraftStrategiesEnum.UPDATE_RECIPIENTS,
        });
      }
    };

    const reloadMessage = () => dispatchProps.reloadMessageTask(message.uid);

    const shareMessage = (options: ShareMessageOptions) =>
      dispatchProps.shareMessageTask(cellsContext, message.uid, options);

    /*
     * User preferences.
     */

    const updateShouldSendOnEnter: (arg: boolean) => void = (newValue) => {
      dispatchProps.updateShouldSendOnEnterForChat(newValue);
    };

    /*
     * Local preferences.
     */

    const setIsFormattingToolbarOpen: (arg: boolean) => void = (value) => {
      dispatchProps.toggleFlag(LocalPreferencesFlagsEnum.FORMATTING_TOOLBAR, value);
    };

    const onPopout = () => {
      dispatchProps.popoutComposer(panelId, message.uid, {
        cellsContext,
        shouldForcePopout: isComposerPopup || isElectron(),
      });
    };

    /*
     * Tags.
     */

    const saveTag: TagSaver = async (template) => {
      const {showInWorkspaces} = template;
      const newTag = await dispatchProps.createTag(template);

      if (showInWorkspaces) {
        dispatchProps.showWorkspaceTag(newTag);
      }

      return newTag;
    };

    /*
     * Timeline Variation.
     */

    const timelineVariation = computeTimelineVariation(channel, stateProps.hasNewSynchronousP3);

    return {
      ...ownProps,
      ...stateProps,
      saveMessage,
      deleteMessage,
      sendMessage,
      reloadMessage,
      shareMessage,
      updateShouldSendOnEnter,
      setIsFormattingToolbarOpen,
      onPopout: canPopout ? onPopout : undefined,
      recipients,
      channel,
      attachments: message.attachments,
      saveTag,
      timelineVariation,
      popoutConversationTransitionViewer: () => {
        if (!ownProps.conversation) {
          return;
        }

        dispatchProps.popoutConversationTransitionViewer(ownProps.conversation.id, recipients);
      },
    };
  },
)(ComposerContainerComponent);
